import { useContext } from 'react'
import { values } from 'ramda'
import { useTranslation } from 'react-i18next'
import { CollapsibleBox } from '@/components/collapsible'
import useCurrentSession from '@/hooks/use-current-session'
import useBetaUser from '@/hooks/use-beta-user'
import { useFlag } from '@/flags'
import PadInfoLandlord from './pad-info-landlord'
import PadInfoTenant from './pad-info-tenant'
import useRentSummary from './use-rent-summary'
import renderCustomField from '../../render-custom-field'
import Context from '../../view/sign/context'

const RentInfo = ({ item, contract, userType, isBillingUser, editable }) => {
  const { t } = useTranslation()
  const session = useCurrentSession()
  const betaUser = useBetaUser()
  const betaUserPad = betaUser.pad
  const currentBetaUser = betaUserPad.includes(parseInt(session.id))
  const showPad = useFlag(['features', 'pad'], false) && currentBetaUser
  const { price, price_frequency, pad_requested, payment_method, payment_method_info } = contract
  const { renderStandardField } = useRentSummary({ contract })
  const isTenant = userType === 'tenant'
  const hasPadRequested = !!pad_requested
  const { tenantPaymentMethodSelection } = useContext(Context)

  // In case of multiple signing tenants, we need to ensure only one can edit PAD info
  const allowedToEdit = editable && isBillingUser

  // If editing use payment method value from dropdown, if viewing or not billing user use payment method from contract
  const paymentMethod = allowedToEdit ? tenantPaymentMethodSelection : payment_method
  const PadMethodSelected = paymentMethod === 'pad'

  const signedNonPadContract = !editable && !PadMethodSelected
  const showTenantPad = hasPadRequested && isTenant && !signedNonPadContract
  const showLandlordPad = hasPadRequested && !isTenant

  const renderFields = field => {
    switch (field.type) {
      case 'FIELD':
        return renderStandardField(field)
      case 'CUSTOM_FIELD':
        return renderCustomField(field, contract)
      default:
        return null
    }
  }
  const listingInfo = {
    price,
    price_frequency,
    price_date_description: contract?.price_date_description,
    listing_contract_id: contract?.id,
    unit_number: contract?.unit?.unit_number,
    full_street_name: contract?.building?.full_street_name,
    city: contract?.building?.city,
    zip: contract?.building?.zip,
    state: contract?.building?.state
  }
  return (
    <CollapsibleBox title={t('b.contract.view.rent_info.subtitle', 'Rent Info')} wrapChildren>
      {values(item.children).map(field => renderFields(field))}
      {showPad && (
        <>
          {showLandlordPad && <PadInfoLandlord paymentMethodInfo={payment_method_info} />}
          {showTenantPad && (
            <PadInfoTenant
              editable={allowedToEdit}
              paymentMethod={paymentMethod}
              paymentMethodInfo={payment_method_info}
              listingInfo={listingInfo}
            />
          )}
        </>
      )}
    </CollapsibleBox>
  )
}

export default RentInfo
